import { z } from 'zod';
import { languageSchema } from '../auth/index';
import { contentLanguageSchema } from '../translation/index';
import { audienceSpecSchema } from '../audience/index';

/**
 * Contracten van de module communication: berichten en de feed van de medewerker.
 *
 * Twee eigenschappen lopen door al deze vormen heen en verklaren de rest:
 *
 * 1. **De ontvangers liggen vast bij publicatie.** Een bericht heeft een lijst met
 *    personen, geen filter dat elke keer opnieuw wordt uitgerekend. Wie later in dienst
 *    komt, ziet het bericht niet alsnog (ADR-0036).
 * 2. **De tekst is een versie.** Elke wijziging maakt een nieuwe versie; wat er stond,
 *    blijft leesbaar. In een fabriek is "wat stond er in dat bericht van maandag" een
 *    vraag met gevolgen.
 */

/**
 * `pending_approval` en `rejected` sinds Stap 15: een bericht van iemand die niet zelf mag
 * publiceren, wacht op een goedkeurder. Een afgewezen bericht blijft bestaan, met de reden.
 */
export const postStatusSchema = z.enum([
  'draft',
  'pending_approval',
  'rejected',
  'published',
  'withdrawn',
]);
export type PostStatus = z.infer<typeof postStatusSchema>;

// --- Beheer ---------------------------------------------------------------------------

/**
 * De tekst van een bericht, in twee vormen.
 *
 * `bodyJson` is wat de editor bewaart en opnieuw kan openen; `bodyHtml` is wat de lezer
 * ziet. De HTML wordt op de server uit de JSON gemaakt en nooit van de client
 * overgenomen — dat is de enige plek waar een sanitizer zin heeft (ADR-0037).
 */
export const postBodySchema = z.object({
  title: z.string().min(1).max(200),
  /** TipTap-document. De server maakt er zelf HTML van. */
  bodyJson: z.unknown(),
});

/**
 * De soort mededeling, zoals ze bovenaan het beginscherm staat.
 *
 * Vier soorten en een vijfde die "geen" betekent. Dat vijfde is de standaard, en daarmee verandert
 * er niets aan een gewoon bericht: alleen wie een categorie kiest, komt in de strook.
 *
 * Waarom een vaste lijst en geen vrij label: de kleur hangt eraan vast (de mockup geeft elk soort
 * een eigen tint), en een vrij label betekent dat elke klant zijn eigen kleurenschema uitvindt. Vier
 * soorten dekken wat een productiebedrijf bovenaan zet: werken aan het gebouw, het menu van de week,
 * een veiligheidsmededeling, en iets van HR.
 */
export const postCategorySchema = z.enum(['geen', 'werken', 'menu', 'veiligheid', 'hr']);
export type PostCategory = z.infer<typeof postCategorySchema>;

/**
 * De periode waarin een mededeling bovenaan hoort te staan.
 *
 * Twee datums en geen vrije tekst. De mockup toont "do 18/09 — vr 19/09", "Deze week" en
 * "Deadline 30/09"; dat zijn drie vormen van hetzelfde, en ze zijn uit twee datums te berekenen.
 * Laat je die regel typen, dan staat er in maart nog "deze week".
 */
export const bulletinPeriodSchema = z.object({
  /** Vanaf wanneer ze zichtbaar is; leeg betekent meteen. */
  showFrom: z.string().date().nullable().default(null),
  /** Tot en met wanneer; leeg betekent tot ze ingetrokken wordt. */
  showUntil: z.string().date().nullable().default(null),
});

/** Eén mededeling zoals de medewerker ze ziet. */
export const bulletinSchema = z.object({
  postId: z.guid(),
  category: postCategorySchema,
  title: z.string(),
  /** Twee regels, afgekapt door de client. */
  excerpt: z.string(),
  showFrom: z.string().date().nullable(),
  showUntil: z.string().date().nullable(),
  publishedAt: z.string().datetime(),
});
export type Bulletin = z.infer<typeof bulletinSchema>;

export const bulletinListSchema = z.object({ items: z.array(bulletinSchema) });
export type BulletinList = z.infer<typeof bulletinListSchema>;

export const createPostRequestSchema = z.object({
  title: z.string().min(1).max(200),
  bodyJson: z.unknown(),
  audience: audienceSpecSchema,
  requiresAcknowledgement: z.boolean().default(false),
  /** De taal waarin dit bericht geschreven is; standaard die van het bedrijf. */
  sourceLanguage: languageSchema.optional(),
  /** Bestanden bij `media`, in de volgorde waarin ze getoond worden. */
  attachmentFileIds: z.array(z.guid()).max(10).default([]),
  /**
   * Dit bericht mag op de digitale schermen (Stap 41).
   *
   * Standaard uit, en met opzet: een bericht dat per ongeluk in de kantine hangt, is niet terug te
   * nemen bij de mensen die er langsliepen. `signage` weigert het bovendien alsnog als het een
   * bevestiging vraagt of een beperkte doelgroep heeft (ADR-0063) — deze vlag zegt alleen dat de
   * schrijver het wil.
   */
  showOnSignage: z.boolean().default(false),
  /**
   * Dit bericht mag op de website van de organisatie (WEB-1, ADR-0097).
   *
   * Standaard uit, en alleen zichtbaar in het opstelscherm als de organisatie haar websitefeed
   * openzette. Zelfs dan komt een bericht er alleen op als het gepubliceerd is, niet ingetrokken, en
   * aan **iedereen** gericht: een bericht voor één groep is op een website een bericht aan de hele
   * wereld.
   */
  showOnWebsite: z.boolean().default(false),
  /**
   * Als dit een mededeling is: de soort en de periode (UI-6).
   *
   * `geen` is de standaard en laat het bericht een gewoon bericht. Kiest de redactie wél een soort,
   * dan komt het bericht bovenaan het beginscherm te staan — en dan is de periode de manier om het
   * er weer af te krijgen zonder het in te trekken.
   */
  category: postCategorySchema.default('geen'),
  showFrom: z.string().date().nullable().default(null),
  showUntil: z.string().date().nullable().default(null),
  /**
   * Verplicht (Stap 16): komt altijd als push door, ook bij wie meldingen voor deze soort bericht
   * uitzette. Voor wat iedereen moet weten - een veiligheidsvoorschrift, een ontruimingsoefening.
   * Stille uren gelden wel; alleen het noodkanaal doorbreekt die.
   */
  mandatory: z.boolean().default(false),
});
export type CreatePostRequest = z.infer<typeof createPostRequestSchema>;

export const updatePostRequestSchema = createPostRequestSchema.partial();
export type UpdatePostRequest = z.infer<typeof updatePostRequestSchema>;

export const withdrawPostRequestSchema = z.object({
  reason: z.string().max(500).nullable().default(null),
});
export type WithdrawPostRequest = z.infer<typeof withdrawPostRequestSchema>;

export const postAttachmentSchema = z.object({
  fileId: z.guid(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  /** Een afbeelding staat in het bericht zelf; een pdf krijgt een knop. */
  isImage: z.boolean(),
  hasThumbnail: z.boolean(),
});
export type PostAttachment = z.infer<typeof postAttachmentSchema>;

/** Eén bericht zoals de schrijver het ziet. */
export const postSchema = z.object({
  id: z.guid(),
  status: postStatusSchema,
  title: z.string(),
  bodyJson: z.unknown(),
  bodyHtml: z.string(),
  version: z.number().int(),
  audience: audienceSpecSchema,
  audienceDescription: z.string(),
  requiresAcknowledgement: z.boolean(),
  /** Verplicht: komt altijd door (Stap 16). */
  mandatory: z.boolean().default(false),
  category: postCategorySchema,
  showFrom: z.string().date().nullable(),
  showUntil: z.string().date().nullable(),
  showOnSignage: z.boolean(),
  /** Op de website van de organisatie (WEB-1); `false` voor een oudere server. */
  showOnWebsite: z.boolean().default(false),
  sourceLanguage: languageSchema,
  attachments: z.array(postAttachmentSchema),
  /** Hoeveel mensen het bericht bij publicatie kregen; null zolang het een concept is. */
  recipientCount: z.number().int().nullable(),
  createdBy: z.guid().nullable(),
  createdAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable(),
  withdrawnAt: z.string().datetime().nullable(),
  withdrawReason: z.string().nullable(),
  /**
   * Het bericht dat dit bericht vervangt (Extra 3, ADR-0070).
   *
   * Gezet door "Corrigeren": een bericht met leesbevestiging ligt na publicatie vast, dus een
   * verbetering is een nieuw bericht. Deze verwijzing maakt van die twee een keten in plaats van
   * twee losse berichten die toevallig op elkaar lijken.
   */
  replacesPostId: z.guid().nullable().default(null),
  /** De goedkeuringsstroom (Stap 15). */
  submittedAt: z.string().datetime().nullable().default(null),
  submittedByName: z.string().nullable().default(null),
  reviewedAt: z.string().datetime().nullable().default(null),
  reviewedByName: z.string().nullable().default(null),
  rejectionReason: z.string().nullable().default(null),
  /** "Namens HR": het afzenderlabel dat de lezer ziet in plaats van de naam van de schrijver. */
  senderLabelId: z.guid().nullable().default(null),
  senderLabelName: z.string().nullable().default(null),
  /** De versie zoals ze ingediend werd, voor de vergelijkweergave van de goedkeurder. */
  submittedVersion: z.number().int().nullable().default(null),
});
export type Post = z.infer<typeof postSchema>;

/**
 * Een bericht dupliceren, eventueel als correctie (Extra 3).
 *
 * Zonder `withdrawOriginal` is het een snelle start voor een gelijkaardig bericht; mét is het een
 * correctie, en dan wordt het origineel ingetrokken en wijst het nieuwe concept ernaar terug.
 */
export const duplicatePostRequestSchema = z.object({
  withdrawOriginal: z.boolean().default(false),
  reason: z.string().max(280).nullable().default(null),
});
export type DuplicatePostRequest = z.infer<typeof duplicatePostRequestSchema>;

/** De lijst voor de schrijver: genoeg om te herkennen, niet de hele tekst. */
export const postSummarySchema = z.object({
  id: z.guid(),
  status: postStatusSchema,
  title: z.string(),
  version: z.number().int(),
  /** De taal waarin de schrijver dit bericht opstelde; nodig om ervanuit te kunnen vertalen. */
  sourceLanguage: languageSchema,
  audienceDescription: z.string(),
  requiresAcknowledgement: z.boolean(),
  showOnSignage: z.boolean(),
  attachmentCount: z.number().int(),
  recipientCount: z.number().int().nullable(),
  createdAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable(),
  /** Wie indiende, voor het tabblad "Te keuren" (Stap 15). */
  submittedByName: z.string().nullable().default(null),
  submittedAt: z.string().datetime().nullable().default(null),
  rejectionReason: z.string().nullable().default(null),
  /** Verplicht: komt altijd door (Stap 16). */
  mandatory: z.boolean().default(false),
  /** De schrijver; voor de melding "reactie op je bericht" (Stap 16). */
  createdBy: z.guid().nullable().default(null),
});
export type PostSummary = z.infer<typeof postSummarySchema>;

export const postListSchema = z.object({
  items: z.array(postSummarySchema),
  nextCursor: z.string().nullable(),
});
export type PostList = z.infer<typeof postListSchema>;

export const postQuerySchema = z.object({
  status: postStatusSchema.optional(),
  search: z.string().max(120).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type PostQuery = z.infer<typeof postQuerySchema>;

export const postVersionSchema = z.object({
  version: z.number().int(),
  title: z.string(),
  bodyHtml: z.string(),
  createdBy: z.guid().nullable(),
  createdAt: z.string().datetime(),
});
export type PostVersion = z.infer<typeof postVersionSchema>;

export const postVersionListSchema = z.object({ items: z.array(postVersionSchema) });
export type PostVersionList = z.infer<typeof postVersionListSchema>;

// --- De feed van de medewerker ---------------------------------------------------------

/**
 * Een kaart in de feed. Geen volledige tekst: op een telefoon met een trage verbinding is
 * een lijst van twintig volledige berichten het verschil tussen een seconde en tien.
 */
/**
 * Wie het bericht stuurde, zoals de ontvanger het ziet.
 *
 * De naam van de **afzender** en niet van een ontvanger: dat onderscheid is precies wat ADR-0014
 * bewaakt. Wie iets naar de hele fabriek stuurt, zet zijn naam eronder; wie iets ontvangt of leest,
 * blijft een getal.
 *
 * De afdeling staat erbij omdat ze de vraag beantwoordt die een operator als eerste stelt: komt dit
 * van HR, van de preventiedienst of van mijn eigen ploegleider? Dat bepaalt of hij nu leest of
 * straks. `null` wanneer de afzender niet meer bestaat of het bericht uit een import komt.
 */
export const feedAuthorSchema = z.object({
  name: z.string(),
  /** De afdeling van de afzender, als die bekend is. */
  department: z.string().nullable(),
});
export type FeedAuthor = z.infer<typeof feedAuthorSchema>;

export const feedItemSchema = z.object({
  postId: z.guid(),
  title: z.string(),
  /** De eerste regels als platte tekst, zonder opmaak. */
  excerpt: z.string(),
  requiresAcknowledgement: z.boolean(),
  attachmentCount: z.number().int(),
  hasImage: z.boolean(),
  /**
   * De eerste bijlage, met haar naam en haar grootte — of `null` als er geen is.
   *
   * Één bijlage en niet alle: de kaart toont één rij, en het aantal staat er al naast. Naam en
   * grootte staan er omdat ze samen de vraag beantwoorden of dit nu te openen valt: een pdf van
   * 240 kB op de werkvloer is iets anders dan een video van 40 MB op mobiele data.
   *
   * Het aantal pagina's staat er niet bij. Dat zou uit het bestand zelf gelezen moeten worden
   * (besluit 5 oktober 2026, zie `docs/design/deviations.md`).
   */
  attachment: z.object({ name: z.string(), sizeBytes: z.number().int() }).nullable().default(null),
  publishedAt: z.string().datetime(),
  /**
   * De versie die op de kaart staat, en de taal waarin ze er staat.
   *
   * Deze twee zijn er sinds UI-7, omdat bevestigen vanaf de kaart kan: het bewijs moet zeggen
   * **welke** tekst iemand bevestigde en in welke taal (ADR-0042). Zonder deze velden zou de app
   * een versienummer moeten gokken, en dan is een bevestiging op een achterhaalde instructie niet
   * meer van een geldige te onderscheiden.
   */
  version: z.number().int().default(1),
  languageShown: z.string().default('nl'),
  author: feedAuthorSchema.nullable(),
  /**
   * Naar wie dit bericht ging, in mensentaal: "Aan alle medewerkers", "Ploeg Nacht".
   *
   * Stond tot UI-7 alleen aan de beheerkant. Op de kaart van de medewerker beantwoordt die regel
   * een vraag die anders blijft hangen: ging dit naar mij persoonlijk, naar mijn ploeg, of naar de
   * hele fabriek? Dat verandert hoe je het leest.
   *
   * Een omschrijving en geen lijst namen: wie het kreeg, is een aantal (ADR-0014).
   */
  audienceDescription: z.string(),
});
export type FeedItem = z.infer<typeof feedItemSchema>;

export const feedPageSchema = z.object({
  items: z.array(feedItemSchema),
  nextCursor: z.string().nullable(),
});
export type FeedPage = z.infer<typeof feedPageSchema>;

export const feedQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  /**
   * Alleen de berichten die ik opzij zette (SCH-1).
   *
   * Een filter op de tijdlijn en geen tweede lijst: een opgeslagen bericht is hetzelfde bericht,
   * met dezelfde doelgroep en dezelfde momentopname van ontvangers (ADR-0036). Een eigen vorm zou
   * betekenen dat er twee plekken zijn waar "mag ik dit zien" uitgerekend wordt.
   *
   * Een opgeslagen bericht dat ingetrokken wordt, valt vanzelf weg: de feed toont alleen
   * gepubliceerde berichten. De rij blijft staan — iemand die een bericht terugzet, vindt zijn
   * markering terug.
   */
  saved: z.coerce.boolean().optional(),
});
export type FeedQuery = z.infer<typeof feedQuerySchema>;

/**
 * Wat er veranderd is sinds de app voor het laatst bijgewerkt werd (Stap 12).
 *
 * Een offline app heeft twee dingen nodig bij het terugkomen: wat is er nieuw of gewijzigd, en wat
 * mag er weg. Dat tweede is het belangrijkste en het makkelijkst te vergeten: een ingetrokken
 * bericht dat op een telefoon blijft staan, is een instructie die niet meer geldt en er toch nog
 * hangt — precies de fout die intrekken hoort te voorkomen.
 *
 * Geen paginering: dit gaat over het verschil sinds een tijdstip, en dat is klein. Wie weken
 * wegbleef, krijgt een volle feed via de gewone weg; daarvoor staat `full` op `true`.
 */
export const feedSyncQuerySchema = z.object({
  /** ISO-tijdstip van de laatste keer bijwerken; leeg betekent "alles opnieuw". */
  since: z.string().datetime().optional(),
});
export type FeedSyncQuery = z.infer<typeof feedSyncQuerySchema>;

export const feedSyncSchema = z.object({
  /** Nieuwe en gewijzigde berichten, nieuwste eerst. */
  changed: z.array(feedItemSchema),
  /** Berichten die van het toestel mogen: ingetrokken, of niet meer voor deze persoon. */
  removed: z.array(z.guid()),
  /**
   * De tijd van de server op dit moment.
   *
   * De app bewaart deze en stuurt hem de volgende keer als `since`. Met de klok van de telefoon
   * zou een toestel dat vijf minuten achterloopt elke keer dezelfde berichten opnieuw halen, en een
   * toestel dat voorloopt zou wijzigingen overslaan.
   */
  serverTime: z.string().datetime(),
  /** `true` als er geen bruikbaar `since` was en dit de volledige stand is. */
  full: z.boolean().default(false),
});
export type FeedSync = z.infer<typeof feedSyncSchema>;

/** Het bericht zoals de medewerker het leest. */
/**
 * Het bericht zoals het in één taal op het scherm van een medewerker komt.
 *
 * Voor het voorbeeld in beheer: een schrijver die een bericht naar acht talen stuurt, hoort te
 * kunnen kijken wat er straks staat. Dat is niet ijdelheid — een vertaling die de opmaak sloopt of
 * een titel die drie keer zo lang wordt, ziet hij hier en niet pas in een klacht van de vloer.
 */
export const postTranslationPreviewSchema = z.object({
  language: contentLanguageSchema,
  title: z.string(),
  bodyHtml: z.string(),
  /** Staat hier werkelijk een vertaling, of het origineel omdat het niet lukte? */
  isTranslated: z.boolean(),
});
export type PostTranslationPreview = z.infer<typeof postTranslationPreviewSchema>;

export const feedPostSchema = z.object({
  /**
   * Of ík dit bericht opzij zette (SCH-1).
   *
   * Staat op het bericht en niet in een eigen aanroep: de actiebalk heeft het meteen nodig, en een
   * tweede rondje voor één booleaan is een rondje te veel. `false` wanneer de module `interaction`
   * uit staat — dan bestaat de knop niet.
   */
  savedByMe: z.boolean().default(false),
  /**
   * Wie het schreef en naar wie het ging (bericht-detail-spec §4.1).
   *
   * Stonden alleen op de kaart in de tijdlijn; de detailpagina toont dezelfde kop, en dan hoort
   * het bericht ze zelf te dragen in plaats van ze uit de feed te moeten lenen — die staat op de
   * detailpagina niet eens geladen.
   */
  author: feedAuthorSchema.nullable().default(null),
  audienceDescription: z.string().nullable().default(null),
  postId: z.guid(),
  title: z.string(),
  bodyHtml: z.string(),
  /**
   * De versie die op het scherm staat.
   *
   * De medewerker stuurt hem mee bij het bevestigen (Stap 10): een bevestiging hoort te gaan over
   * de tekst die hij werkelijk las, en niet over een tekst die intussen gewijzigd is.
   */
  version: z.number().int(),
  requiresAcknowledgement: z.boolean(),
  attachments: z.array(postAttachmentSchema),
  /** De taal waarin de schrijver het bericht opstelde. */
  sourceLanguage: languageSchema,
  /**
   * De taal waarin de tekst hierboven staat.
   *
   * Gelijk aan `sourceLanguage` als er niet vertaald is. De medewerker stuurt deze code mee bij het
   * bevestigen (Stap 10/11): bewijs van "ik heb het gelezen" hoort te zeggen wélke tekst hij las.
   */
  languageShown: contentLanguageSchema,
  /** Staat er een vertaling? Dan hoort de balk "automatisch vertaald" erbij. */
  isTranslated: z.boolean().default(false),
  /**
   * De lezer vroeg een vertaling en die kwam niet.
   *
   * Dan staat er het origineel plús een regel die zegt dat de vertaling er even niet is. Zwijgen zou
   * betekenen dat iemand een tekst in een vreemde taal leest zonder te weten waarom — en het
   * verschil tussen "dit bericht is Nederlands" en "de vertaling is stuk" bepaalt of hij wacht of
   * een collega vraagt.
   */
  translationUnavailable: z.boolean().default(false),
  /**
   * Het bericht dat dit bericht vervangt (Extra 3, ADR-0070).
   *
   * Staat er alleen bij een **ingetrokken** bericht waarvan de correctie gepubliceerd is en waar
   * deze lezer ook bij hoort. Zonder die laatste voorwaarde zou een ingetrokken bericht de titel
   * van een bericht tonen dat niet voor hem bedoeld was - en een titel is al inhoud.
   *
   * Zonder deze verwijzing leest een ingetrokken bericht als "dit klopt niet meer", en moet de
   * lezer zelf uitzoeken wat er dan wel klopt.
   */
  replacedBy: z.object({ postId: z.guid(), title: z.string() }).nullable().default(null),
  publishedAt: z.string().datetime(),
});
export type FeedPost = z.infer<typeof feedPostSchema>;

// --- Goedkeuring en afzenderlabels (Stap 15) ------------------------------------------------

/**
 * Goedkeuren, eventueel met aanpassingen en namens een afzenderlabel.
 *
 * Aanpassingen worden een nieuwe versie vóór de publicatie, en vallen dus niet onder het slot op
 * gepubliceerde berichten met leesbevestiging (ADR-0070).
 */
export const approvePostRequestSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  bodyJson: z.unknown().optional(),
  senderLabelId: z.guid().nullable().optional(),
});
export type ApprovePostRequest = z.infer<typeof approvePostRequestSchema>;

/** Afwijzen vraagt een reden; zonder reden is het `422` (Stap 15). */
export const rejectPostRequestSchema = z.object({
  reason: z.string().max(1000).default(''),
});
export type RejectPostRequest = z.infer<typeof rejectPostRequestSchema>;

export const senderLabelSchema = z.object({
  id: z.guid(),
  name: z.record(z.string(), z.string()),
  active: z.boolean(),
});
export type SenderLabel = z.infer<typeof senderLabelSchema>;

export const senderLabelListSchema = z.object({ items: z.array(senderLabelSchema) });
export type SenderLabelList = z.infer<typeof senderLabelListSchema>;

export const createSenderLabelRequestSchema = z.object({
  name: z.object({
    nl: z.string().trim().min(1).max(60),
    fr: z.string().trim().max(60).optional(),
    en: z.string().trim().max(60).optional(),
  }),
});
export type CreateSenderLabelRequest = z.infer<typeof createSenderLabelRequestSchema>;

export const updateSenderLabelRequestSchema = z.object({
  name: createSenderLabelRequestSchema.shape.name.optional(),
  active: z.boolean().optional(),
});
export type UpdateSenderLabelRequest = z.infer<typeof updateSenderLabelRequestSchema>;

export * from './website-feed';
